import { useQuery } from "@tanstack/react-query";

import type {
  BookingOperationalInsightDto,
  CommunityFeedOperationalInsightDto,
  EventOperationalInsightDto,
  TicketOperationalInsightDto,
} from "@/api/types";
import { canAccessAnalytics, canAccessOperationalAnalyticsSummary } from "@/components/ProjectSidebar/permissions";
import { formatSignedChange } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useConfig } from "@/providers/config/useConfig";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type OperationalAnalyticsMetric, OperationalAnalyticsSummary } from "./OperationalAnalyticsSummary";

const DATE_RANGE = "last6Months" as const;
const percentage = (value: number | undefined) => (value == null ? "-" : `${Math.round(value)}%`);
const decimal = (value: number | undefined) => value?.toFixed(1) ?? "-";
const rating = (value: number | undefined) => (value == null ? "-" : `${value}/10`);

const LOADING_VALUE = "...";

function loadingMetric(label: string, footer?: string): OperationalAnalyticsMetric {
  return { label, value: undefined, renderFooter: footer ? () => footer : undefined };
}

function useTicketLoadingMetrics(): OperationalAnalyticsMetric[] {
  const { t } = useTranslation();

  return [
    loadingMetric(
      t("page.analytics-tickets.overview.open"),
      t("component.operational-analytics.tickets.open-change", { change: LOADING_VALUE }),
    ),
    loadingMetric(
      t("page.analytics-tickets.overview.resolution-time"),
      t("page.analytics-tickets.overview.p90-resolution", {
        value: LOADING_VALUE,
        change: LOADING_VALUE,
        direction: LOADING_VALUE,
      }),
    ),
    loadingMetric(
      t("page.analytics-tickets.overview.satisfaction"),
      t("page.analytics-tickets.overview.rated-tickets", {
        percentage: LOADING_VALUE,
        count: LOADING_VALUE,
        change: LOADING_VALUE,
      }),
    ),
  ];
}

function useCommunityFeedLoadingMetrics(isSentimentAvailable: boolean): OperationalAnalyticsMetric[] {
  const { t } = useTranslation();

  const metrics: OperationalAnalyticsMetric[] = [
    loadingMetric(
      t("page.analytics-community-feed.overview.visible-posts"),
      t("page.analytics-community-feed.overview.previous-period-change", { change: LOADING_VALUE }),
    ),
    loadingMetric(
      t("page.analytics-community-feed.overview.resident-posts"),
      t("page.analytics-community-feed.overview.resident-share", { percentage: LOADING_VALUE }),
    ),
    loadingMetric(
      t("page.analytics-community-feed.overview.average-interactions"),
      t("page.analytics-community-feed.overview.previous-period-change", { change: LOADING_VALUE }),
    ),
  ];

  if (isSentimentAvailable) {
    metrics.push(
      loadingMetric(
        t("page.analytics-community-feed.sentiment.average-score"),
        t("component.operational-analytics.community-feed.sentiment-sample", { count: LOADING_VALUE }),
      ),
    );
  }

  return metrics;
}

function useEventLoadingMetrics(): OperationalAnalyticsMetric[] {
  const { t } = useTranslation();

  return [
    loadingMetric(t("page.analytics-events.overview.events")),
    loadingMetric(t("page.analytics-events.overview.registrations")),
    loadingMetric(t("page.analytics-events.overview.unique-participants")),
    loadingMetric(t("page.analytics-events.overview.without-registrations")),
  ];
}

function useBookingLoadingMetrics(): OperationalAnalyticsMetric[] {
  const { t } = useTranslation();

  return [
    loadingMetric(t("page.analytics-bookings.overview.active-assets")),
    loadingMetric(
      t("page.analytics-bookings.overview.bookings"),
      t("page.analytics-bookings.overview.previous-period-change", { change: LOADING_VALUE }),
    ),
    loadingMetric(
      t("page.analytics-bookings.overview.unique-bookers"),
      t("page.analytics-bookings.overview.resident-reach", {
        percentage: LOADING_VALUE,
        change: LOADING_VALUE,
      }),
    ),
    loadingMetric(
      t("page.analytics-bookings.overview.utilization"),
      t("page.analytics-bookings.overview.percentage-point-change", { change: LOADING_VALUE }),
    ),
    loadingMetric(
      t("page.analytics-bookings.overview.cancellation-rate"),
      t("page.analytics-bookings.overview.cancelled-change", { count: LOADING_VALUE, change: LOADING_VALUE }),
    ),
  ];
}

function useTicketMetrics(data: TicketOperationalInsightDto | undefined): OperationalAnalyticsMetric[] | undefined {
  const { i18n, t } = useTranslation();
  if (!data) {
    return undefined;
  }

  const medianChange =
    data.medianResolutionDays === undefined || data.previousPeriod.medianResolutionDays === undefined
      ? undefined
      : Math.round((data.medianResolutionDays - data.previousPeriod.medianResolutionDays) * 10) / 10;
  const ratingChange =
    data.averageRating === undefined || data.previousPeriod.averageRating === undefined
      ? "-"
      : formatSignedChange(data.averageRating - data.previousPeriod.averageRating, i18n.language);
  const ratedPercentage = data.createdTickets === 0 ? 0 : Math.round((100 * data.ratedTickets) / data.createdTickets);
  const p90ResolutionDays = data.p90ResolutionDays;

  return [
    {
      label: t("page.analytics-tickets.overview.open"),
      value: data.openTickets,
      renderFooter: () =>
        t("component.operational-analytics.tickets.open-change", {
          change: formatSignedChange(data.openTicketsChange, i18n.language),
        }),
    },
    {
      label: t("page.analytics-tickets.overview.resolution-time"),
      value: data.medianResolutionDays,
      renderValue: (value) => (value === undefined ? "-" : `${value} ${t("page.analytics-tickets.value.days-unit")}`),
      renderFooter:
        p90ResolutionDays === undefined
          ? undefined
          : () =>
              medianChange === undefined
                ? t("page.analytics-tickets.overview.p90-resolution-value", { value: p90ResolutionDays })
                : t("page.analytics-tickets.overview.p90-resolution", {
                    value: p90ResolutionDays,
                    change: Math.abs(medianChange),
                    direction:
                      medianChange <= 0
                        ? t("page.analytics-tickets.overview.faster")
                        : t("page.analytics-tickets.overview.slower"),
                  }),
    },
    {
      label: t("page.analytics-tickets.overview.satisfaction"),
      value: data.averageRating,
      renderValue: rating,
      renderFooter: () =>
        t("page.analytics-tickets.overview.rated-tickets", {
          count: data.ratedTickets,
          percentage: ratedPercentage,
          change: ratingChange,
        }),
    },
  ];
}

function useCommunityFeedMetrics(
  data: CommunityFeedOperationalInsightDto | undefined,
  isSentimentAvailable: boolean,
): OperationalAnalyticsMetric[] | undefined {
  const { i18n, t } = useTranslation();
  if (!data) {
    return undefined;
  }

  const visiblePostsChange = data.visiblePosts - data.previousPeriod.visiblePosts;
  const interactionsChange = data.averageInteractionsPerPost - data.previousPeriod.averageInteractionsPerPost;
  const residentPostPercentage =
    data.visiblePosts === 0 ? 0 : Math.round((100 * data.residentPosts) / data.visiblePosts);

  const metrics: OperationalAnalyticsMetric[] = [
    {
      label: t("page.analytics-community-feed.overview.visible-posts"),
      value: data.visiblePosts,
      renderFooter: () =>
        t("page.analytics-community-feed.overview.previous-period-change", {
          change: formatSignedChange(visiblePostsChange, i18n.language),
        }),
    },
    {
      label: t("page.analytics-community-feed.overview.resident-posts"),
      value: data.residentPosts,
      renderFooter: () =>
        t("page.analytics-community-feed.overview.resident-share", { percentage: residentPostPercentage }),
    },
    {
      label: t("page.analytics-community-feed.overview.average-interactions"),
      value: data.averageInteractionsPerPost,
      renderValue: decimal,
      renderFooter: () =>
        t("page.analytics-community-feed.overview.previous-period-change", {
          change: formatSignedChange(interactionsChange, i18n.language),
        }),
    },
  ];

  if (isSentimentAvailable) {
    metrics.push({
      label: t("page.analytics-community-feed.sentiment.average-score"),
      value: data.sentiment.averageScore,
      renderValue: rating,
      renderFooter: data.sentiment.hasSufficientData
        ? () =>
            t("component.operational-analytics.community-feed.sentiment-sample", {
              count: data.sentiment.analyzedResidentPosts,
            })
        : undefined,
    });
  }

  return metrics;
}

function useEventMetrics(data: EventOperationalInsightDto | undefined): OperationalAnalyticsMetric[] | undefined {
  const { t } = useTranslation();
  if (!data) {
    return undefined;
  }

  return [
    { label: t("page.analytics-events.overview.events"), value: data.occurrences },
    { label: t("page.analytics-events.overview.registrations"), value: data.registrations },
    { label: t("page.analytics-events.overview.unique-participants"), value: data.uniqueParticipants },
    {
      label: t("page.analytics-events.overview.without-registrations"),
      value: data.zeroRegistrationOccurrences,
    },
  ];
}

function useBookingMetrics(data: BookingOperationalInsightDto | undefined): OperationalAnalyticsMetric[] | undefined {
  const { i18n, t } = useTranslation();
  if (!data) {
    return undefined;
  }

  return [
    { label: t("page.analytics-bookings.overview.active-assets"), value: data.activeAssets },
    {
      label: t("page.analytics-bookings.overview.bookings"),
      value: data.bookings,
      renderFooter: () =>
        t("page.analytics-bookings.overview.previous-period-change", {
          change: formatSignedChange(data.bookings - data.previousPeriod.bookings, i18n.language),
        }),
    },
    {
      label: t("page.analytics-bookings.overview.unique-bookers"),
      value: data.uniqueBookers,
      renderFooter: () =>
        t("page.analytics-bookings.overview.resident-reach", {
          percentage: data.bookerReachPercentage,
          total: data.eligibleResidents,
          change: formatSignedChange(data.uniqueBookers - data.previousPeriod.uniqueBookers, i18n.language),
        }),
    },
    {
      label: t("page.analytics-bookings.overview.utilization"),
      value: data.utilizationPercentage,
      renderValue: percentage,
      renderFooter: () =>
        t("page.analytics-bookings.overview.percentage-point-change", {
          change: formatSignedChange(
            data.utilizationPercentage - data.previousPeriod.utilizationPercentage,
            i18n.language,
          ),
        }),
    },
    {
      label: t("page.analytics-bookings.overview.cancellation-rate"),
      value: data.cancellationPercentage,
      renderValue: percentage,
      renderFooter: () =>
        t("page.analytics-bookings.overview.cancelled-change", {
          count: data.cancelledBookings,
          change: formatSignedChange(
            data.cancellationPercentage - data.previousPeriod.cancellationPercentage,
            i18n.language,
          ),
        }),
    },
  ];
}

/** Shows the six-month ticket aggregates for the current project. */
export function TicketOperationalAnalyticsSummary(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const projectId = useProjectId();
  const hasPermission = usePermission();
  const analyticsQueries = useAnalyticsQueries();
  const canViewSummary = hasPermission(canAccessOperationalAnalyticsSummary);
  const query = useQuery({ ...analyticsQueries.ticketSummary(), enabled: canViewSummary });
  const metrics = useTicketMetrics(query.data);
  const loadingMetrics = useTicketLoadingMetrics();

  if (!canViewSummary) {
    return null;
  }

  return (
    <OperationalAnalyticsSummary
      module="tickets"
      scope="project"
      projectId={projectId}
      title={t("component.operational-analytics.tickets.title")}
      metrics={metrics}
      loadingMetrics={loadingMetrics}
      detailsHref={
        hasPermission(canAccessAnalytics)
          ? `${routes.analytics.ticketReporting({ slug })}?dateRange=${DATE_RANGE}`
          : undefined
      }
      isLoading={query.isLoading}
      error={query.error}
      onReload={() => void query.refetch()}
    />
  );
}

/** Shows the six-month community-feed aggregates for the current project. */
export function CommunityFeedOperationalAnalyticsSummary(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const projectId = useProjectId();
  const hasPermission = usePermission();
  const analyticsQueries = useAnalyticsQueries();
  const isSentimentAvailable = useConfig("isCommunityFeedSentimentAvailable");
  const canViewSummary = hasPermission(canAccessOperationalAnalyticsSummary);
  const query = useQuery({ ...analyticsQueries.communityFeedSummary(), enabled: canViewSummary });
  const metrics = useCommunityFeedMetrics(query.data, isSentimentAvailable);
  const loadingMetrics = useCommunityFeedLoadingMetrics(isSentimentAvailable);

  if (!canViewSummary) {
    return null;
  }

  return (
    <OperationalAnalyticsSummary
      module="communityFeed"
      scope="project"
      projectId={projectId}
      title={t("component.operational-analytics.community-feed.title")}
      metrics={metrics}
      loadingMetrics={loadingMetrics}
      detailsHref={
        hasPermission(canAccessAnalytics)
          ? `${routes.analytics.communityFeedReporting({ slug })}?dateRange=${DATE_RANGE}`
          : undefined
      }
      isLoading={query.isLoading}
      error={query.error}
      onReload={() => void query.refetch()}
    />
  );
}

/** Shows the six-month event aggregates for the current project. */
export function EventOperationalAnalyticsSummary(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const projectId = useProjectId();
  const hasPermission = usePermission();
  const analyticsQueries = useAnalyticsQueries();
  const canViewSummary = hasPermission(canAccessOperationalAnalyticsSummary);
  const query = useQuery({ ...analyticsQueries.eventSummary(), enabled: canViewSummary });
  const metrics = useEventMetrics(query.data);
  const loadingMetrics = useEventLoadingMetrics();

  if (!canViewSummary) {
    return null;
  }

  return (
    <OperationalAnalyticsSummary
      module="events"
      scope="project"
      projectId={projectId}
      title={t("component.operational-analytics.events.title")}
      metrics={metrics}
      loadingMetrics={loadingMetrics}
      detailsHref={
        hasPermission(canAccessAnalytics)
          ? `${routes.analytics.eventReporting({ slug })}?dateRange=${DATE_RANGE}`
          : undefined
      }
      isLoading={query.isLoading}
      error={query.error}
      onReload={() => void query.refetch()}
    />
  );
}

/** Shows the six-month booking aggregates for the current project. */
export function BookingOperationalAnalyticsSummary(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const projectId = useProjectId();
  const hasPermission = usePermission();
  const analyticsQueries = useAnalyticsQueries();
  const canViewSummary = hasPermission(canAccessOperationalAnalyticsSummary);
  const query = useQuery({ ...analyticsQueries.bookingSummary(), enabled: canViewSummary });
  const metrics = useBookingMetrics(query.data);
  const loadingMetrics = useBookingLoadingMetrics();

  if (!canViewSummary) {
    return null;
  }

  return (
    <OperationalAnalyticsSummary
      module="bookings"
      scope="project"
      projectId={projectId}
      title={t("component.operational-analytics.bookings.title")}
      metrics={metrics}
      loadingMetrics={loadingMetrics}
      detailsHref={
        hasPermission(canAccessAnalytics)
          ? `${routes.analytics.bookingReporting({ slug })}?dateRange=${DATE_RANGE}`
          : undefined
      }
      isLoading={query.isLoading}
      error={query.error}
      onReload={() => void query.refetch()}
    />
  );
}

interface PortfolioTicketOperationalAnalyticsSummaryProps {
  /** Project IDs already narrowed to the ticket list's effective scope. */
  projectIds: string[];
  /** Details URL supplied only after the caller has authorized every scoped project. */
  detailsHref?: string;
}

/** Shows ticket aggregates for the portfolio ticket list's authorized effective project scope. */
export function PortfolioTicketOperationalAnalyticsSummary({
  projectIds,
  detailsHref,
}: PortfolioTicketOperationalAnalyticsSummaryProps): React.ReactNode {
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const analyticsQueries = useAnalyticsQueries();
  const canViewSummary = hasPermission(canAccessOperationalAnalyticsSummary);
  const query = useQuery({
    ...analyticsQueries.portfolioTicketSummary({ projectIds, dateRange: DATE_RANGE }),
    enabled: canViewSummary && projectIds.length > 0,
  });
  const metrics = useTicketMetrics(query.data);
  const loadingMetrics = useTicketLoadingMetrics();

  if (!canViewSummary) {
    return null;
  }

  return (
    <OperationalAnalyticsSummary
      module="tickets"
      scope="portfolio"
      projectIds={projectIds}
      title={t("component.operational-analytics.tickets.title")}
      metrics={metrics}
      loadingMetrics={loadingMetrics}
      detailsHref={detailsHref}
      isLoading={query.isLoading}
      error={query.error}
      onReload={() => void query.refetch()}
    />
  );
}
